import { Extension, type Editor } from '@tiptap/vue-3';
import { PluginKey } from '@tiptap/pm/state';
import {
	type FileHandlerPluginOptions,
	fileHandlerPlugin,
} from '@/features/App/AppTextEditor/plugins/FileHandlerPlugin';

type ImageUrl = string;
type Base64Image = string;

export type ImageFileHandlerOptions = Omit<
	FileHandlerPluginOptions,
	'key' | 'editor'
>;

/**
 * TODO: replace once we agree on extracting file embedding to SPA
 */
export const mockUploadHandler: (
	file: Blob | Base64Image
) => Promise<ImageUrl | undefined> = (file) => {
	const isUsingUpload = false;
	return Promise.resolve(
		file && isUsingUpload
			? 'https://localhost.scoro.dev/files/embed/5ohzpdoy9d44s0ckcss04ggogog80kck'
			: undefined
	);
};

export const ImageFileHandler = Extension.create<ImageFileHandlerOptions>({
	name: 'imageFileHandler',

	addOptions: () => ({
		onPaste: (editor, files, htmlContent) => {
			files.forEach((file) => {
				if (htmlContent) {
					// if there is htmlContent, stop manual insertion & let other extensions handle insertion via inputRule
					// you could extract the pasted file from this url string and upload it to a server for example
					return false;
				}

				const fileReader = new FileReader();

				fileReader.readAsDataURL(file);
				fileReader.onload = () => {
					editor
						.chain()
						.insertContentAt(editor.state.selection.anchor, {
							type: 'image',
							attrs: {
								src: fileReader.result,
							},
						})
						.focus()
						.run();
				};
			});
		},
		onDrop: (editor, files, pos) => {
			files?.forEach(async (file) => {
				const fileReader = new FileReader();

				const imageUrl = await mockUploadHandler(file);

				if (imageUrl) {
					editor
						.chain()
						.insertContentAt(pos, {
							type: 'image',
							attrs: {
								src: imageUrl,
							},
						})
						.focus()
						.run();

					return;
				}

				fileReader.readAsDataURL(file);
				fileReader.onload = () => {
					editor
						.chain()
						.insertContentAt(pos, {
							type: 'image',
							attrs: {
								src: fileReader.result,
							},
						})
						.focus()
						.run();
				};
			});
		},
		allowedMimeTypes: [
			'image/png',
			'image/jpeg',
			'image/gif',
			'image/webp',
		],
	}),

	addProseMirrorPlugins() {
		return [
			fileHandlerPlugin({
				key: new PluginKey(this.name),
				editor: this.editor as Editor,
				allowedMimeTypes: this.options.allowedMimeTypes,
				onDrop: this.options.onDrop,
				onPaste: this.options.onPaste,
			}),
		];
	},
});
